import * as React from 'react';
import { useState, useMemo } from 'react';

import type Search from 'views/logic/search/Search';
import type { PreviewJobStatus, DataLakeOption, DataLakeInfoArray } from 'data-lake/preview/Types';
import useFeature from 'hooks/useFeature';
import { VALID_INITIAL_SEARCH_STATUSES } from 'data-lake/preview/Constants';
import usePluginEntities from 'hooks/usePluginEntities';
import type { DataLakePlugin } from 'data-lake/Types';
import { INTERNAL_DATA_LAKE_ID, INTERNAL_DATA_LAKE_STORAGE_TYPE } from 'data-lake/preview/types/internal/Constants';
import type { QueryString } from 'views/logic/queries/types';

import type { ConfiguredDataLakesContextValue } from './ConfiguredDataLakesContext';
import ConfiguredDataLakesContext from './ConfiguredDataLakesContext';

const INTERNAL_DATA_LAKE_INFO = {
  id: INTERNAL_DATA_LAKE_ID,
  type: INTERNAL_DATA_LAKE_STORAGE_TYPE,
  storageType: INTERNAL_DATA_LAKE_STORAGE_TYPE,
  title: 'Internal Data Lake',
  active: true,
} as const;

const isValidDataLakeId = (activeDataLakes: DataLakeInfoArray, dataLakeId: string) =>
  activeDataLakes.some(({ id }) => id === dataLakeId);

const isValidQueryTypeForDataLake = (
  dataLakePlugins: Array<DataLakePlugin>,
  type: string,
): type is QueryString['type'] => dataLakePlugins.some(({ queryType }) => queryType === type);

const findIdInQueryString = (activeSearch: Search): string => {
  const { query } = activeSearch.queries.first();

  return 'data_lake_id' in query && query.data_lake_id ? query.data_lake_id : INTERNAL_DATA_LAKE_ID;
};
const initiallySelectedDataLakeId = (
  featureIsEnabled: boolean,
  initialUrlParams: { dataLakeId: string },
  activeSearch: Search,
  searchJobStatus: PreviewJobStatus,
  activeDataLakes: DataLakeInfoArray,
  dataLakePlugins: Array<DataLakePlugin>,
) => {
  if (!featureIsEnabled) {
    return INTERNAL_DATA_LAKE_ID;
  }

  // get active data lake id from URL query params
  if (initialUrlParams?.dataLakeId && isValidDataLakeId(activeDataLakes, initialUrlParams?.dataLakeId)) {
    return initialUrlParams.dataLakeId;
  }

  // get active data lake id from active search
  if (VALID_INITIAL_SEARCH_STATUSES.includes(searchJobStatus) && activeSearch) {
    const query = activeSearch.queries.first();
    const queryType = query.query.type;
    if (isValidQueryTypeForDataLake(dataLakePlugins, queryType)) {
      return findIdInQueryString(activeSearch);
    }
  }

  if (activeDataLakes?.length > 0) {
    if (activeDataLakes.find(({ id: dataLakeId }) => dataLakeId === INTERNAL_DATA_LAKE_ID)) {
      return INTERNAL_DATA_LAKE_ID;
    }

    return activeDataLakes[0].id;
  }

  throw new Error('Expected at least one data lake to be configured.');
};

type Props = React.PropsWithChildren<{
  initialUrlParams: { dataLakeId: string };
  activeSearch: Search;
  searchJobStatus: PreviewJobStatus;
  activeDataLakes: DataLakeInfoArray;
}>;

const ConfiguredDataLakesProvider = ({
  children = undefined,
  initialUrlParams,
  activeSearch,
  searchJobStatus,
  activeDataLakes: activeDataLakesProp,
}: Props) => {
  const featureIsEnabled = useFeature('external_data_lake_search');
  const activeDataLakes = useMemo(
    () => (featureIsEnabled ? activeDataLakesProp : [INTERNAL_DATA_LAKE_INFO]),
    [activeDataLakesProp, featureIsEnabled],
  );
  const dataLakePlugins = usePluginEntities('dataLakeStorageTypes');
  const [selectedDataLakeId, selectDataLakeId] = useState<DataLakeOption['value']>(
    initiallySelectedDataLakeId(
      featureIsEnabled,
      initialUrlParams,
      activeSearch,
      searchJobStatus,
      activeDataLakes,
      dataLakePlugins,
    ),
  );
  const contextValue = useMemo<ConfiguredDataLakesContextValue>(
    () => ({
      selectDataLake: selectDataLakeId,
      dataLakes: activeDataLakes,
      selectedDataLake: activeDataLakes.find(({ id }) => id === selectedDataLakeId),
    }),
    [activeDataLakes, selectedDataLakeId],
  );

  return <ConfiguredDataLakesContext.Provider value={contextValue}>{children}</ConfiguredDataLakesContext.Provider>;
};

export default ConfiguredDataLakesProvider;
